import { Button, Div, H5, IconButton, P, ToolTip } from '@driftt/tide-core'
import { Modal, ModalBody, ModalFooter, ModalTitle } from '@driftt/tide-modal'
import { Select } from '@driftt/tide-selects'
import cx from 'classnames'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { fetchActiveUrls } from 'api/reach'
import Portal from 'components/Portal'
import { copyToClipboard } from 'utils'

import './style.css'

const TestBotModal = ({ isOpen, onClose, slug, specificUrls }) => {
  const [hasCopiedLink, setHasCopiedLink] = useState(false)
  const [activeUrls, setActiveUrls] = useState(specificUrls)
  const [selectValue, setSelectValue] = useState({})

  useEffect(() => {
    async function getActiveUrls() {
      let urls = specificUrls
      try {
        const { data } = await fetchActiveUrls()
        urls = data?.activeUrls || []
      } catch (error) {
        console.warn(error)
      }

      setActiveUrls(urls)
    }

    if (isEmpty(specificUrls)) {
      getActiveUrls()
    }

    setSelectValue(() => {
      const initialSelectValue = isEmpty(specificUrls) ? '' : specificUrls[0]
      return { label: initialSelectValue, value: initialSelectValue }
    })
  }, [specificUrls])

  const optionsForSelect = useMemo(() => {
    return isEmpty(activeUrls)
      ? []
      : activeUrls.map((url) => {
          return { label: url, value: url }
        })
  }, [activeUrls])

  const handleChange = useCallback(
    (option) => {
      setSelectValue(option || {})
      setHasCopiedLink(false)
    },
    [setSelectValue, setHasCopiedLink]
  )

  const getTestLink = useMemo(() => {
    if (isEmpty(selectValue)) {
      return ''
    }

    if (
      selectValue.value.startsWith('https://') ||
      selectValue.value.startsWith('http://')
    ) {
      return `${selectValue.value}#${slug}`
    }

    return `https://${selectValue.value}#${slug}`
  }, [selectValue, slug])

  const handleClickCopy = useCallback(() => {
    if (selectValue?.value) {
      copyToClipboard(getTestLink)
      setHasCopiedLink(true)
    }
  }, [selectValue, getTestLink])

  const handleClose = () => {
    const url = selectValue.value ?? ''
    setSelectValue({})
    onClose(url)
  }

  const handleClickTest = () => {
    window.open(getTestLink, '_blank')
    handleClose()
  }

  return (
    <Portal elementSelector="#pew-modal-root">
      <Modal
        open={isOpen}
        onClose={handleClose}
        size={Modal.SIZE_SMALL}
        className="testbot-modal"
      >
        <ModalTitle>Test bot</ModalTitle>
        <ModalBody>
          <P>
            To test, we’ll create a unique URL– one where your bot is visible on
            the selected page. This bot is not actually live and data captured
            here won’t affect bot metrics.
          </P>
          <Div className="testbot-modal--select">
            <Select
              options={optionsForSelect}
              placeholder="Select or search…"
              label="Select a page to test your bot"
              value={selectValue}
              onChange={handleChange}
              isClearable={!isEmpty(selectValue)}
              menuPortalTarget={document.body}
              selectStyles={{
                menuPortal: (base) => ({ ...base, zIndex: 100000 }),
              }}
              isSearchable
            />
          </Div>

          {selectValue.value && (
            <Div className="testbot-modal--copy">
              <H5>Share test bot</H5>
              <Div className="testbot-modal-copy--container">
                <P
                  onClick={handleClickCopy}
                  className={cx('testbot-modal-copy--p', {
                    'copied-link': hasCopiedLink,
                  })}
                >
                  {selectValue.value}#{slug}
                </P>
                {!hasCopiedLink ? (
                  <ToolTip hover component={'Copy link'} flow="up">
                    <IconButton
                      icon="link-1"
                      onClick={handleClickCopy}
                      size="medium"
                    />
                  </ToolTip>
                ) : (
                  <ToolTip hover component={'Copied!'} flow="up">
                    <IconButton icon="check" size="medium" />
                  </ToolTip>
                )}
              </Div>
            </Div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            type="primary"
            onClick={handleClickTest}
            disabled={!selectValue.value}
            className="testbot-modal--testButton"
          >
            Test bot on page
          </Button>
          <Button type="tertiary" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Portal>
  )
}

TestBotModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  slug: PropTypes.string.isRequired,
  specificUrls: PropTypes.arrayOf(PropTypes.string),
}

TestBotModal.defaultTypes = {
  specificUrls: [],
}

export default TestBotModal
